import { Certificate, CertificateType } from '../types/certificate'
import { formatDate } from '../utils/locale'
import './CertificateList.css'

interface CertificateListProps {
  certificates: Certificate[]
  onEdit: (certificate: Certificate) => void
  onDelete: (id: string, name: string) => void
  onActivate: (id: string) => void
  onRevoke: (id: string) => void
  onValidate: (id: string) => void
  filterType?: CertificateType
}

export function CertificateList({
  certificates,
  onEdit,
  onDelete,
  onActivate,
  onRevoke,
  onValidate,
  filterType,
}: CertificateListProps) {

  const filteredCertificates = filterType
    ? certificates.filter((c) => c.type === filterType)
    : certificates

  const getStatusBadge = (status: Certificate['status']) => {
    const statusClasses = {
      active: 'badge-active',
      expired: 'badge-expired',
      revoked: 'badge-revoked',
      pending: 'badge-pending',
    }
    return <span className={`badge ${statusClasses[status]}`}>{status}</span>
  }

  const isExpiringSoon = (validTo: string) => {
    const expiryDate = new Date(validTo)
    const thirtyDaysFromNow = new Date()
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)
    return expiryDate <= thirtyDaysFromNow && expiryDate > new Date()
  }

  const isExpired = (validTo: string) => {
    return new Date(validTo) < new Date()
  }

  if (filteredCertificates.length === 0) {
    return (
      <div className="empty-state">
        <p>No certificates found</p>
        {filterType && <p className="empty-hint">Try removing the filter or add a new certificate</p>}
      </div>
    )
  }

  return (
    <div className="certificate-list" role="region" aria-label="Certificate list">
      <div className="certificates-grid">
        {filteredCertificates.map((certificate) => (
          <div
            key={certificate.id}
            className={`certificate-card ${isExpired(certificate.validTo) ? 'expired' : ''} ${isExpiringSoon(certificate.validTo) ? 'expiring-soon' : ''}`}
            role="article"
            aria-label={`Certificate: ${certificate.name}`}
          >
            <div className="certificate-card-header">
              <div className="certificate-type-badge">
                {certificate.type}
              </div>
              {getStatusBadge(certificate.status)}
            </div>

            <div className="certificate-card-body">
              <h3>{certificate.name}</h3>
              {certificate.description && (
                <p className="certificate-description">{certificate.description}</p>
              )}

              <div className="certificate-info">
                <div className="info-item">
                  <span className="info-label">Valid From:</span>
                  <span className="info-value">{formatDate(certificate.validFrom)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Valid To:</span>
                  <span className={`info-value ${isExpired(certificate.validTo) ? 'expired' : ''} ${isExpiringSoon(certificate.validTo) ? 'expiring-soon' : ''}`}>
                    {formatDate(certificate.validTo)}
                  </span>
                </div>
                {certificate.serialNumber && (
                  <div className="info-item">
                    <span className="info-label">Serial Number:</span>
                    <span className="info-value">{certificate.serialNumber}</span>
                  </div>
                )}
                {certificate.issuer && (
                  <div className="info-item">
                    <span className="info-label">Issuer:</span>
                    <span className="info-value">{certificate.issuer}</span>
                  </div>
                )}
                {certificate.cipherSummary && (
                  <div className="info-item">
                    <span className="info-label">Cipher:</span>
                    <span className="info-value">{certificate.cipherSummary}</span>
                  </div>
                )}
                {certificate.legacyCipher !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Legacy:</span>
                    <span className="info-value">{certificate.legacyCipher ? 'Yes' : 'No'}</span>
                  </div>
                )}
              </div>

              {isExpiringSoon(certificate.validTo) && !isExpired(certificate.validTo) && (
                <div className="warning-banner">
                  ⚠️ Certificate expires within 30 days
                </div>
              )}

              {isExpired(certificate.validTo) && (
                <div className="error-banner">
                  ⚠️ Certificate has expired
                </div>
              )}
            </div>

            <div className="certificate-card-actions">
              <button
                onClick={() => onValidate(certificate.id)}
                className="btn-action btn-validate"
                aria-label={`Validate ${certificate.name}`}
              >
                Validate
              </button>
              <button
                onClick={() => onEdit(certificate)}
                className="btn-action btn-edit"
                aria-label={`Edit ${certificate.name}`}
              >
                Edit
              </button>
              {certificate.isActive ? (
                <button
                  onClick={() => onRevoke(certificate.id)}
                  className="btn-action btn-revoke"
                  aria-label={`Revoke ${certificate.name}`}
                >
                  Revoke
                </button>
              ) : (
                <button
                  onClick={() => onActivate(certificate.id)}
                  className="btn-action btn-activate"
                  aria-label={`Activate ${certificate.name}`}
                >
                  Activate
                </button>
              )}
              <button
                onClick={() => onDelete(certificate.id, certificate.name)}
                className="btn-action btn-delete"
                aria-label={`Delete ${certificate.name}`}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}



































